import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Thin wrapper server fn kept for existing callers (Survey.tsx, InquiryForm.tsx).
 * The primary registration flow now runs GHL inline inside submitSurveyLead so
 * a client-side redirect can never cancel the sync mid-flight.
 */
const attributionSchema = z
  .object({
    utm_source: z.string().max(300).nullable().optional(),
    utm_medium: z.string().max(300).nullable().optional(),
    utm_campaign: z.string().max(300).nullable().optional(),
    utm_content: z.string().max(300).nullable().optional(),
    utm_term: z.string().max(300).nullable().optional(),
    fbclid: z.string().max(500).nullable().optional(),
    gclid: z.string().max(500).nullable().optional(),
    landing_path: z.string().max(1000).nullable().optional(),
    referrer: z.string().max(1000).nullable().optional(),
    fbp: z.string().max(200).nullable().optional(),
    fbc: z.string().max(500).nullable().optional(),
  })
  .partial()
  .optional()
  .nullable();

const leadSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(40).optional().nullable(),
  role: z.string().max(100).optional().nullable(),
  area: z.string().max(200).optional().nullable(),
  deals_12mo: z.string().max(50).optional().nullable(),
  lead_gen_sources: z.array(z.string().max(100)).max(20).optional().nullable(),
  website_status: z.string().max(200).optional().nullable(),
  website: z.string().max(500).optional().nullable(),
  followup_system: z.string().max(300).optional().nullable(),
  motivation: z.string().max(1000).optional().nullable(),
  lead_score: z.number().min(0).max(10).optional().nullable(),
  timezone: z.string().max(100).optional().nullable(),
  page_url: z.string().max(1000).optional().nullable(),
  user_agent: z.string().max(1000).optional().nullable(),
  variant: z.enum(["A", "B", "C", "D"]).optional().nullable(),
  attribution: attributionSchema,
  first_attribution: attributionSchema,
  inquiry_id: z.string().uuid().optional().nullable(),
});

export const syncLeadToGhl = createServerFn({ method: "POST" })
  .inputValidator((input) => leadSchema.parse(input))
  .handler(async ({ data }) => {
    const { runGhlSync } = await import("./ghl.server");
    const { inquiry_id, ...lead } = data;
    return runGhlSync(lead, { inquiryId: inquiry_id ?? null });
  });
